import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, FileText } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/api";
import PagesEditor from "@/components/PagesEditor";

const inputCls = "w-full px-4 py-2.5 rounded-xl border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 text-sm";

const SITE_FIELDS = [
  ["phone", "Phone"], ["email", "Email"],
  ["address_mr", "Address (मराठी)"], ["address_hi", "Address (हिंदी)"], ["address_en", "Address (English)"],
  ["twitter", "Twitter URL"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"],
  ["tagline_mr", "Tagline (मराठी)"], ["tagline_hi", "Tagline (हिंदी)"], ["tagline_en", "Tagline (English)"],
  ["hero_title_mr", "Hero Title (मराठी)"], ["hero_title_hi", "Hero Title (हिंदी)"], ["hero_title_en", "Hero Title (English)"],
  ["hero_subtitle_mr", "Hero Subtitle (मराठी)"], ["hero_subtitle_hi", "Hero Subtitle (हिंदी)"], ["hero_subtitle_en", "Hero Subtitle (English)"],
];

export default function AdminDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState({});
  const [members, setMembers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [media, setMedia] = useState([]);
  const [levelFilter, setLevelFilter] = useState("");
  const [search, setSearch] = useState("");
  const [donSettings, setDonSettings] = useState({ upi_id: "", account_name: "", account_number: "", ifsc: "", bank_name: "", branch: "" });
  const [siteSettings, setSiteSettings] = useState(null);
  const [newMedia, setNewMedia] = useState({ title: "", kind: "image", url: "" });
  const [mediaFile, setMediaFile] = useState(null);

  const mediaSrc = (m) => (m.url && m.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : m.url);

  const levelLabels = {
    state: t.level_state, district: t.level_district, block: t.level_block,
    city: t.level_city, village: t.level_village,
  };

  const loadMembers = () => {
    const params = {};
    if (levelFilter) params.level = levelFilter;
    if (search) params.search = search;
    api.get("/admin/members", { params }).then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e)));
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/settings/donation").then((r) => setDonSettings(r.data)).catch(() => {});
    api.get("/settings/site").then((r) => setSiteSettings(r.data)).catch(() => {});
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMembers(); }, [levelFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "donations") api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
    if (tab === "messages") api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
    if (tab === "media") api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  }, [tab]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/members/${id}/status`, { status });
      toast.success(status === "approved" ? t.approved : t.pending);
      loadMembers();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDonSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings/donation", donSettings);
      toast.success(t.profile_saved);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveSiteSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings/site", siteSettings);
      toast.success(t.profile_saved);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const addMedia = async (e) => {
    e.preventDefault();
    try {
      if (mediaFile) {
        const fd = new FormData();
        fd.append("title", newMedia.title);
        fd.append("file", mediaFile);
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/admin/media", newMedia);
      }
      toast.success(t.add_media);
      setNewMedia({ title: "", kind: "image", url: "" });
      setMediaFile(null);
      const r = await api.get("/media");
      setMedia(r.data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteMedia = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`);
      setMedia(media.filter((m) => m.id !== id));
      toast.success(t.delete);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const tabs = [
    { id: "members", label: t.tab_members }, { id: "donations", label: t.tab_donations },
    { id: "messages", label: t.tab_messages }, { id: "settings", label: t.tab_settings },
    { id: "media", label: t.tab_media }, { id: "site", label: t.tab_site },
    { id: "pages", label: t.tab_pages },
  ];

  const statCards = [
    { label: t.tab_members, value: stats.members ?? 0 },
    { label: t.tab_donations, value: stats.donations ?? 0 },
    { label: t.tab_messages, value: stats.messages ?? 0 },
    { label: t.tab_media, value: stats.media ?? 0 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-emerald-950 mb-2" data-testid="admin-dashboard-heading">
        {t.admin_dashboard}
      </h1>
      <p className="text-sm text-slate-500 mb-8">{t.welcome}, {user?.name}</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-5 text-center">
            <div className="font-heading text-3xl font-extrabold text-emerald-900" data-testid={`admin-stat-${i}`}>{s.value}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap mb-6" data-testid="admin-tabs">
        {tabs.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`admin-tab-${tb.id}`}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
              tab === tb.id ? "bg-emerald-900 text-white" : "bg-white border border-emerald-200 text-emerald-900 hover:bg-emerald-50"
            }`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "members" && (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6" data-testid="admin-members-panel">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadMembers()}
              placeholder={t.search_placeholder} className={`${inputCls} sm:max-w-xs`} data-testid="admin-search-input" />
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} className={`${inputCls} sm:max-w-xs`} data-testid="admin-level-filter">
              <option value="">{t.all_levels}</option>
              {Object.entries(levelLabels).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <button onClick={loadMembers} data-testid="admin-search-btn"
              className="px-5 py-2.5 rounded-xl bg-emerald-900 text-white text-sm font-semibold hover:bg-emerald-800">
              {t.submit}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-members-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-emerald-100">
                  <th className="py-3 pr-4">{t.form_name}</th>
                  <th className="py-3 pr-4">{t.member_id}</th>
                  <th className="py-3 pr-4">{t.mobile_label}</th>
                  <th className="py-3 pr-4">{t.level}</th>
                  <th className="py-3 pr-4">{t.district}</th>
                  <th className="py-3 pr-4">{t.status_label}</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-emerald-50" data-testid={`member-row-${m.member_id}`}>
                    <td className="py-3 pr-4 font-semibold text-emerald-950">{m.name}</td>
                    <td className="py-3 pr-4 font-mono text-xs">{m.member_id}</td>
                    <td className="py-3 pr-4">{m.mobile}</td>
                    <td className="py-3 pr-4">{levelLabels[m.level] || m.level}</td>
                    <td className="py-3 pr-4">{m.district}</td>
                    <td className="py-3 pr-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                        m.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>{m.status === "approved" ? t.approved : t.pending}</span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => setStatus(m.id, m.status === "approved" ? "pending" : "approved")}
                        data-testid={`toggle-status-${m.member_id}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${
                          m.status === "approved" ? "bg-amber-600 text-white" : "bg-emerald-800 text-white"
                        }`}>
                        {m.status === "approved" ? t.mark_pending : t.approve}
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="members-empty">{t.no_data}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "donations" && (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6" data-testid="admin-donations-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-emerald-100">
                  <th className="py-3 pr-4">{t.form_name}</th>
                  <th className="py-3 pr-4">{t.mobile_label}</th>
                  <th className="py-3 pr-4">{t.amount}</th>
                  <th className="py-3 pr-4">{t.message}</th>
                  <th className="py-3">{t.joining_date}</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((d) => (
                  <tr key={d.id} className="border-b border-emerald-50" data-testid={`donation-row-${d.id}`}>
                    <td className="py-3 pr-4 font-semibold text-emerald-950">{d.name}</td>
                    <td className="py-3 pr-4">{d.mobile}</td>
                    <td className="py-3 pr-4 font-bold text-emerald-800">₹{d.amount}</td>
                    <td className="py-3 pr-4 text-slate-500">{d.message || "—"}</td>
                    <td className="py-3 text-slate-500 text-xs">{new Date(d.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
                {donations.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-slate-400" data-testid="donations-empty">{t.no_data}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-4" data-testid="admin-messages-panel">
          {messages.map((m) => (
            <div key={m.id} className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6" data-testid={`message-card-${m.id}`}>
              <div className="flex justify-between flex-wrap gap-2 mb-2">
                <span className="font-heading font-bold text-emerald-950">{m.name}</span>
                <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("en-IN")}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">{m.mobile} {m.email ? `| ${m.email}` : ""}</p>
              <p className="text-sm text-slate-700">{m.message}</p>
            </div>
          ))}
          {messages.length === 0 && <p className="text-center text-slate-400 py-10" data-testid="messages-empty">{t.no_data}</p>}
        </div>
      )}

      {tab === "settings" && (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 max-w-2xl" data-testid="admin-settings-panel">
          <form onSubmit={saveDonSettings} className="space-y-4" data-testid="donation-settings-form">
            {[
              ["upi_id", t.upi_id], ["account_name", t.account_name], ["account_number", t.account_number],
              ["ifsc", t.ifsc], ["bank_name", t.bank_name], ["branch", t.branch],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{label}</label>
                <input value={donSettings[k]} onChange={(e) => setDonSettings({ ...donSettings, [k]: e.target.value })}
                  className={inputCls} data-testid={`setting-${k}-input`} />
              </div>
            ))}
            <button type="submit" data-testid="settings-save-btn"
              className="px-8 py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all">
              {t.save}
            </button>
          </form>
        </div>
      )}

      {tab === "site" && siteSettings && (
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 max-w-3xl" data-testid="admin-site-settings-panel">
          <form onSubmit={saveSiteSettings} className="space-y-4" data-testid="site-settings-form">
            {SITE_FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{label}</label>
                {k.startsWith("address") || k.startsWith("hero_subtitle") ? (
                  <textarea rows={2} value={siteSettings[k] || ""} onChange={(e) => setSiteSettings({ ...siteSettings, [k]: e.target.value })} className={inputCls} data-testid={`site-${k}-input`} />
                ) : (
                  <input value={siteSettings[k] || ""} onChange={(e) => setSiteSettings({ ...siteSettings, [k]: e.target.value })} className={inputCls} data-testid={`site-${k}-input`} />
                )}
              </div>
            ))}
            <button type="submit" data-testid="site-settings-save-btn"
              className="px-8 py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all">
              {t.save}
            </button>
          </form>
        </div>
      )}

      {tab === "pages" && <PagesEditor />}

      {tab === "media" && (
        <div className="space-y-8" data-testid="admin-media-panel">
          <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 max-w-2xl">
            <h3 className="font-heading font-bold text-emerald-950 mb-5">{t.add_media}</h3>
            <form onSubmit={addMedia} className="space-y-4" data-testid="media-add-form">
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.media_title_field} *</label>
                <input required value={newMedia.title} onChange={(e) => setNewMedia({ ...newMedia, title: e.target.value })}
                  className={inputCls} data-testid="media-title-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.media_type}</label>
                  <select value={newMedia.kind} onChange={(e) => setNewMedia({ ...newMedia, kind: e.target.value })}
                    className={inputCls} data-testid="media-kind-select">
                    <option value="image">Image</option>
                    <option value="youtube">YouTube</option>
                    <option value="pdf">PDF</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.media_url} *</label>
                  <input required={!mediaFile} value={newMedia.url} onChange={(e) => setNewMedia({ ...newMedia, url: e.target.value })}
                    className={inputCls} data-testid="media-url-input" placeholder="https://..." />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.upload_file}</label>
                <input type="file" accept=".pdf,image/*,video/*" onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                  className={inputCls} data-testid="media-file-input" />
              </div>
              <button type="submit" data-testid="media-add-btn"
                className="px-8 py-3 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white font-bold transition-all">
                {t.add_media}
              </button>
            </form>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {media.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-emerald-100 shadow-sm overflow-hidden" data-testid={`admin-media-item-${m.id}`}>
                {m.kind === "youtube" ? (
                  <div className="aspect-video"><iframe src={m.url} title={m.title} className="w-full h-full" allowFullScreen /></div>
                ) : m.kind === "pdf" ? (
                  <a href={mediaSrc(m)} target="_blank" rel="noreferrer" data-testid={`admin-media-pdf-${m.id}`}
                    className="aspect-video bg-red-50 flex flex-col items-center justify-center gap-2 hover:bg-red-100 transition-colors">
                    <FileText className="w-12 h-12 text-red-600" />
                    <span className="text-xs font-semibold text-red-700">{t.view_pdf}</span>
                  </a>
                ) : m.kind === "video" ? (
                  <video controls className="w-full aspect-video bg-black" data-testid={`admin-media-video-${m.id}`}>
                    <source src={mediaSrc(m)} />
                  </video>
                ) : (
                  <img src={mediaSrc(m)} alt={m.title} className="w-full aspect-video object-cover" />
                )}
                <div className="p-4 flex items-center justify-between gap-2">
                  <span className="font-heading font-semibold text-emerald-950 text-sm">{m.title}</span>
                  <button onClick={() => deleteMedia(m.id)} data-testid={`media-delete-${m.id}`}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {media.length === 0 && <p className="text-slate-400 py-6" data-testid="media-admin-empty">{t.no_data}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
